import { useCallback, useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  getRouteSalesTargets,
  saveRouteSalesTargets,
} from "../../services/routeSalesTarget";
import {
  RouteSalesTargetMonth,
  RouteSalesTargetSkipped,
} from "../../types/route-sales-target.type";
import { pushGlobalError } from "../../errors/globalError";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { todayInBangkok } from "../../utils/date";
import { useLeavePageGuard } from "../../utils/hooks/useLeavePageGuard";
import ConfirmModal from "../ConfirmModal";
import CodeMultiSelect from "../DemandFactor/CodeMultiSelect";
import { useBranches } from "../SpecialOrder/useBranches";
import ExpenseMonthPicker from "../DailyExpense/ExpenseMonthPicker";
import SkeletonCard from "../DailyExpense/SkeletonCard";
import StateCard from "../DailyExpense/StateCard";
import { useLatestAnswer } from "../DailyExpense/useLatestAnswer";
import {
  API_MONTH_FORMAT,
  ROUTE_SALES_TARGET_MENU_TITLE,
} from "../DailyExpense/constants";
import { FillMonthDialog, PasteDialog } from "./TargetDialogs";
import TargetTable, { CellError } from "./TargetTable";
import {
  EMPTY_TARGET_TEXT,
  FILTER_MISSING_TEXT,
  SHOW_ALL_TEXT,
} from "./constants";
import {
  TargetDraft,
  copyFromLastMonth,
  draftToCells,
  missingDayCountOf,
  missingRouteCountOf,
  parseAmount,
  setCell,
  tableAsExcel,
} from "./routeSalesTargetDraft";

interface TargetParams {
  month: string;
  branchCodes: string[];
}

type OpenDialog = "FILL" | "PASTE" | null;

const COPY_FAILED_TEXT = "คัดลอกไม่สำเร็จ";

/** S6 เป้าหมายสายส่ง — head office sets each route's daily sales target for a month. */
export default function RouteSalesTargetScreen() {
  const branches = useBranches(true);
  const [month, setMonth] = useState<Dayjs>(() =>
    todayInBangkok().startOf("month"),
  );
  const [branchCodes, setBranchCodes] = useState<string[]>([]);
  const [draft, setDraft] = useState<TargetDraft>({});
  const [errors, setErrors] = useState<Record<string, CellError>>({});
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [skipped, setSkipped] = useState<RouteSalesTargetSkipped[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const yearMonth = month.format(API_MONTH_FORMAT);
  const today = todayInBangkok().format("YYYY-MM-DD");

  const params = useMemo<TargetParams>(
    () => ({ month: yearMonth, branchCodes }),
    [yearMonth, branchCodes],
  );
  const load = useCallback(
    async (query: TargetParams): Promise<RouteSalesTargetMonth> => {
      const response = await getRouteSalesTargets(
        query.month,
        query.branchCodes,
      );
      if (!response.data.data) throw new Error("Empty response");
      return response.data.data;
    },
    [],
  );
  const answer = useLatestAnswer(params, load);
  const isDirty = Object.keys(draft).length > 0;
  const leaveGuard = useLeavePageGuard(isDirty);

  useEffect(() => {
    setDraft({});
    setErrors({});
    setSkipped([]);
  }, [params]);

  const allRows = useMemo(() => answer.data?.rows ?? [], [answer.data]);
  const visibleRows = useMemo(
    () =>
      onlyMissing
        ? allRows
            .map((branch) => ({
              ...branch,
              routes: branch.routes.filter(
                (route) => missingDayCountOf(route, draft, today) > 0,
              ),
            }))
            .filter((branch) => branch.routes.length > 0)
        : allRows,
    [allRows, onlyMissing, draft, today],
  );
  const allRoutes = useMemo(
    () => allRows.flatMap((branch) => branch.routes),
    [allRows],
  );
  const missingRouteCount = missingRouteCountOf(allRows, draft, today);
  const branchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        code: branch.branch_code,
        label: `${branch.branch_code} - ${branch.branch_name}`,
      })),
    [branches],
  );

  const handleMonthChange = (value: Dayjs | null) => {
    if (value?.isValid()) setMonth(value.startOf("month"));
  };
  const handleCommit = (
    route: Parameters<typeof setCell>[1],
    dayIndex: number,
    text: string,
  ) => {
    const day = route.days[dayIndex];
    const key = `${route.route_uuid}|${day.date}`;
    const parsed =
      text.trim() === EMPTY_TARGET_TEXT
        ? { ok: true as const, value: null }
        : parseAmount(text);
    if (!parsed.ok) {
      setErrors((previous) => ({
        ...previous,
        [key]: { text, message: parsed.message },
      }));
      return;
    }
    setErrors(({ [key]: _cleared, ...rest }) => rest);
    setDraft((previous) => setCell(previous, route, day, parsed.value));
  };
  const closeDialog = () => setDialog(null);
  const applyDraft = (next: TargetDraft) => {
    setDraft(next);
    setDialog(null);
  };
  const openFill = () => setDialog("FILL");
  const openPaste = () => setDialog("PASTE");
  const clearSkipped = () => setSkipped([]);
  const toggleMissing = () => setOnlyMissing((previous) => !previous);

  const handleCopyPrevious = async () => {
    try {
      const response = await getRouteSalesTargets(
        month.subtract(1, "month").format(API_MONTH_FORMAT),
        branchCodes,
      );
      const previousRoutes = (response.data.data?.rows ?? []).flatMap(
        (branch) => branch.routes,
      );
      setDraft((previous) =>
        copyFromLastMonth(previous, allRoutes, previousRoutes, today),
      );
      pushGlobalSuccess("คัดลอกจากเดือนก่อนแล้ว (ยังไม่บันทึก)");
    } catch {
      // The interceptor has already reported the failed read; the draft stays as it was.
    }
  };
  const handleCopyTable = async () => {
    try {
      await navigator.clipboard.writeText(tableAsExcel(allRoutes, draft));
      pushGlobalSuccess("คัดลอกตารางแล้ว");
    } catch {
      pushGlobalError(COPY_FAILED_TEXT);
    }
  };
  const handleSave = async () => {
    if (Object.keys(errors).length > 0) {
      pushGlobalError(
        `ตรวจตัวเลขอีกครั้ง · มี ${Object.keys(errors).length} ช่องที่ไม่ถูกต้อง`,
      );
      return;
    }
    setIsSaving(true);
    try {
      const response = await saveRouteSalesTargets(draftToCells(draft));
      const result = response.data.data;
      if (!result) return;
      pushGlobalSuccess(`บันทึกเป้าแล้ว · ${result.saved.length} ช่อง`);
      answer.reload();
      setDraft({});
      setErrors({});
      setSkipped(result.skipped);
    } catch {
      // The interceptor shows the server's message; the typed values stay for another try.
    } finally {
      setIsSaving(false);
    }
  };

  const renderBody = () => {
    if (answer.isLoading) return <SkeletonCard />;
    if (answer.isFailed || !answer.data) {
      return (
        <StateCard
          title="โหลดเป้าหมายไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่อีกครั้ง"
          onRetry={answer.reload}
        />
      );
    }
    if (allRows.length === 0)
      return (
        <StateCard
          title="ไม่มีสายส่งในสาขาที่เลือก"
          text="แสดงเฉพาะสายหลักที่ไม่ใช่สาย test"
        />
      );
    return (
      <TargetTable
        branches={visibleRows}
        draft={draft}
        errors={errors}
        today={today}
        onCommit={handleCommit}
      />
    );
  };

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600}>
          {ROUTE_SALES_TARGET_MENU_TITLE}
        </Typography>
        <ExpenseMonthPicker
          value={month}
          onChange={handleMonthChange}
          allowFuture
        />
        <Stack sx={{ minWidth: 280, flex: 1 }}>
          <CodeMultiSelect
            label="สาขา (ไม่เลือก = ทุกสาขา)"
            options={branchOptions}
            value={branchCodes}
            onChange={setBranchCodes}
          />
        </Stack>
        <Button
          variant="contained"
          disabled={!isDirty || isSaving}
          onClick={handleSave}
        >
          บันทึก
        </Button>
      </Stack>
      {missingRouteCount > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={toggleMissing}>
              {onlyMissing ? SHOW_ALL_TEXT : FILTER_MISSING_TEXT}
            </Button>
          }
        >
          เดือนนี้ยังมี {missingRouteCount} สายที่ไม่มีเป้า
        </Alert>
      )}
      {skipped.length > 0 && (
        <Alert severity="warning" onClose={clearSkipped}>
          ข้าม {skipped.length} ช่อง:{" "}
          {skipped
            .slice(0, 5)
            .map((item) => `${item.date} ${item.message}`)
            .join(" · ")}
        </Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          variant="outlined"
          onClick={openFill}
          disabled={allRoutes.length === 0}
        >
          ใส่ค่าเดียวทั้งเดือน
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyPrevious}
          disabled={allRoutes.length === 0}
        >
          คัดลอกจากเดือนก่อน
        </Button>
        <Button
          variant="outlined"
          onClick={openPaste}
          disabled={allRoutes.length === 0}
        >
          วางจาก Excel
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyTable}
          disabled={allRoutes.length === 0}
        >
          คัดลอกตารางเป็น Excel
        </Button>
      </Stack>
      {renderBody()}
      {dialog === "FILL" && (
        <FillMonthDialog
          routes={allRoutes}
          draft={draft}
          today={today}
          onApply={applyDraft}
          onClose={closeDialog}
        />
      )}
      {dialog === "PASTE" && (
        <PasteDialog
          routes={allRoutes}
          draft={draft}
          today={today}
          onApply={applyDraft}
          onClose={closeDialog}
        />
      )}
      <ConfirmModal
        open={leaveGuard.isAskingToLeave}
        title="ออกโดยไม่บันทึก?"
        description="ค่าที่แก้ในตารางยังไม่ได้บันทึก ถ้าออกตอนนี้ค่าที่แก้จะหายไป"
        cancelLabel="อยู่ต่อ"
        confirmLabel="ออกโดยไม่บันทึก"
        confirmColor="error"
        onClose={leaveGuard.stay}
        onConfirm={leaveGuard.leave}
      />
    </Stack>
  );
}
